'use client';
import React, { useState, useEffect, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import { Search, X, BadgeCheck, Loader2 } from 'lucide-react';
import Link from 'next/link';

interface UserResult {
  id: string;
  username: string | null;
  display_name: string | null;
  avatar_url: string | null;
  follower_count: number;
  role: string;
  is_verified: boolean;
}

export default function UserSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UserResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setOpen(false);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      const supabase = createClient();
      const { data } = await supabase
        .from('profiles')
        .select('id, username, display_name, avatar_url, follower_count, role, is_verified')
        .ilike('username', `%${query.trim()}%`)
        .eq('is_active', true)
        .order('follower_count', { ascending: false })
        .limit(8);
      setResults((data as UserResult[]) || []);
      setOpen(true);
      setLoading(false);
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function clearSearch() {
    setQuery('');
    setResults([]);
    setOpen(false);
  }

  return (
    <div ref={containerRef} className="relative w-full max-w-sm">
      {/* Input */}
      <div className="relative flex items-center">
        <Search size={15} className="absolute left-3 text-charcoal-400 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder="Search users…"
          className="w-full pl-9 pr-8 py-2 text-sm bg-white border border-cream-300 rounded-xl text-charcoal-900 placeholder-charcoal-400 focus:outline-none focus:ring-2 focus:ring-gold-400 focus:border-transparent transition-all"
        />
        {(query || loading) && (
          <button
            onClick={clearSearch}
            className="absolute right-2.5 text-charcoal-400 hover:text-charcoal-600 transition-colors"
            aria-label="Clear search"
          >
            {loading ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <X size={14} />
            )}
          </button>
        )}
      </div>

      {/* Dropdown */}
      {open && (
        <div className="absolute top-full mt-2 left-0 right-0 bg-white border border-cream-300 rounded-2xl shadow-lg z-50 overflow-hidden fade-in">
          {results.length === 0 ? (
            <p className="text-xs text-charcoal-400 text-center py-4 px-4">
              No users found for &ldquo;{query}&rdquo;
            </p>
          ) : (
            <ul className="py-2 max-h-72 overflow-y-auto scrollbar-hide">
              {results.map((user) => (
                <li key={user.id}>
                  <Link
                    href={`/profile/${user.username || user.id}`}
                    onClick={() => { setOpen(false); setQuery(''); }}
                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-cream-100 transition-colors"
                  >
                    {/* Avatar */}
                    {user.avatar_url ? (
                      <img
                        src={user.avatar_url}
                        alt={user.display_name || user.username || ''}
                        className="w-9 h-9 rounded-full object-cover shrink-0"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-gold-300 to-gold-500 flex items-center justify-center text-xs font-bold text-white shrink-0">
                        {(user.display_name || user.username || '?').charAt(0).toUpperCase()}
                      </div>
                    )}

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1">
                        <span className="text-sm font-semibold text-charcoal-900 truncate">
                          {user.display_name || user.username}
                        </span>
                        {(user.is_verified || user.role === 'icon') && (
                          <BadgeCheck size={14} className="text-gold-500 shrink-0" aria-label="Verified" />
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-xs text-charcoal-400">@{user.username}</span>
                        <span className="text-charcoal-300 text-xs">·</span>
                        <span className="text-xs text-charcoal-400">
                          {user.follower_count.toLocaleString()} followers
                        </span>
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
